// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Avatar.tsx
 * @input Uses React, HTMLAttributes, ReactNode, useState
 * @output Exports Avatar component, AvatarProps, AvatarSize types
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Avatar/Avatar.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Avatar/index.ts (exports if types change)
 * - /apps/storybook/stories/Avatar.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Avatar/ (showcase blocks)
 */
import { useMemo, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, typographyVars, fontWeightVars, radiusVars } from "../theme/tokens.stylex.js";
import { AvatarSizeContext } from "./AvatarSizeContext.js";
import { useAvatarGroup } from "../AvatarGroup/AvatarGroupContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * The offset ratio for positioning elements on a circle's edge at 45°.
 *
 * For a square with side length S containing an inscribed circle of diameter S,
 * a diagonal line from corner to corner intersects the circle at:
 *   x = S/2 × (1 ± 1/√2)
 *
 * The distance from the corner to this intersection point (along each axis) is:
 *   S/2 × (1 - 1/√2) ≈ 0.146S
 *
 * This constant represents that ratio: (1 - 1/√2) / 2 ≈ 0.146
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CIRCLE_EDGE_OFFSET_RATIO = (1 - 1 / Math.SQRT2) / 2;

/**
 * The ratio of font size to avatar size for initials.
 *
 * At 40%, two-letter initials fit comfortably within the circle with adequate
 * padding. This ratio provides good legibility across all avatar sizes:
 *   - 24px avatar → 9.6px font
 *   - 48px avatar → 19.2px font
 *   - 128px avatar → 51.2px font
 */
const INITIALS_FONT_SIZE_RATIO = 0.4;

/**
 * Named size options
 */

/**
 * Numeric size options (in pixels)
 */

/**
 * Avatar size - can be a named size or a specific pixel value
 */

/**
 * Resolves named sizes to their numeric pixel values
 */
export function resolveSize(size) {
  if (typeof size === 'number') {
    return size;
  }
  switch (size) {
    case 'tiny':
      return 20;
    case 'xsmall':
      return 24;
    case 'small':
      return 36;
    case 'medium':
      return 48;
    case 'large':
      return 128;
  }
}

/**
 * Base styles for the avatar
 * Uses a wrapper/content structure so status isn't clipped by overflow:hidden
 */
const styles = {
  wrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    $$css: true
  },
  content: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kaIpWk: "xjspbzw",
    kVQacm: "xb3r6kr",
    kfSwDN: "x87ps6o",
    $$css: true
  },
  fallback: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kWkggS: "x17x4s8c",
    kMwMTN: "xv1l7n4",
    kMv6JI: "x9ynric",
    k63SB2: "x1e4wzip",
    kP9fke: "xtvhhri",
    $$css: true
  },
  status: {
    kVAEAm: "x10l6tqk",
    $$css: true
  }
};

/**
 * Dynamic styles that depend on the avatar size
 */
const _temp = {
  kGuDYH: "xdmh292",
  "$$css": true
};
const _temp2 = {
  krVfgx: "x1nqzi6q",
  kCIrl2: "x7ok3n0",
  k3aq6I: "x4v1no5",
  "$$css": true
};
const dynamicStyles = {
  size: size => [{
    kzqmXN: size != null ? "x5lhr3w" : size,
    kZKoxP: size != null ? "x16ye13r" : size,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }],
  fontSize: size => [_temp, {
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size * INITIALS_FONT_SIZE_RATIO)
  }],
  statusPosition: size => [_temp2, {
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size * CIRCLE_EDGE_OFFSET_RATIO),
    "--x-right": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size * CIRCLE_EDGE_OFFSET_RATIO)
  }]
};
const BORDER_WIDTH = 2;
const groupStyles = {
  ring: {
    kaIpWk: "xjspbzw",
    kMzoRj: "xdh2fpr",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1touxvs",
    kWkggS: "x10xzikg",
    kB7OPa: "x1afcbsf",
    $$css: true
  },
  overlap: {
    keTefX: "x1sik18w",
    $$css: true
  }
};
const _temp3 = {
  "--_avatar-group-overlap": "xlz5hwt",
  "$$css": true
};
const groupDynamicStyles = {
  overlap: offset => [_temp3, {
    "--x---_avatar-group-overlap": `${offset}px` != null ? `${offset}px` : undefined
  }]
};
/**
 * Generates initials from a name string.
 * Takes the first letter of the first two words.
 * @example
 * ```
 * getInitials('John Doe')
 * getInitials('Alice')
 * ```
 */
function getInitials(name) {
  const words = name.trim().split(/\s+/);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

/**
 * Default person icon SVG for when no image or name is provided
 */
function DefaultIcon({
  size
}) {
  return /*#__PURE__*/_jsx("svg", {
    width: size * 0.6,
    height: size * 0.6,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    })
  });
}

/**
 * Avatar component for displaying user profile pictures.
 *
 * Displays an image when available, falling back to initials derived from
 * the name prop, or a generic person icon if neither is provided.
 *
 * @example
 * ```
 * <Avatar src="/user.jpg" name="John Doe" />
 * <Avatar name="Jane Smith" size="large" />
 * <Avatar src="/user.jpg" status={<OnlineIndicator />} />
 * ```
 */
export function Avatar({
  alt,
  'data-testid': testId,
  fallbackSrc,
  name,
  size = 'small',
  src,
  status,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  // Track the exact src that failed (rather than a boolean) so a changed
  // src/fallbackSrc gets a fresh load attempt instead of the stale error.
  const [erroredSrc, setErroredSrc] = useState(undefined);
  const [erroredFallbackSrc, setErroredFallbackSrc] = useState(undefined);
  const showImage = src && erroredSrc !== src;
  const showFallbackImage = !showImage && fallbackSrc && erroredFallbackSrc !== fallbackSrc;
  const showInitials = !showImage && !showFallbackImage && name;
  const showIcon = !showImage && !showFallbackImage && !name;

  // A meaningful accessible name comes from `alt` or `name`. With neither, the
  // avatar is decorative — expose it as `presentation`/`aria-hidden` rather than
  // announcing a meaningless generic "Avatar" (obs-9).
  const accessibleName = alt || name;
  const isDecorative = !accessibleName;
  const avatarGroup = useAvatarGroup();
  const resolvedSize = avatarGroup?.size ?? size;
  const numericSize = useMemo(() => resolveSize(resolvedSize), [resolvedSize]);
  return /*#__PURE__*/_jsx(AvatarSizeContext, {
    value: numericSize,
    children: /*#__PURE__*/_jsxs("div", {
      ...props,
      ref: ref,
      role: isDecorative ? 'presentation' : 'img',
      "aria-label": isDecorative ? undefined : accessibleName,
      "aria-hidden": isDecorative || undefined,
      "data-testid": testId,
      ...mergeProps(themeProps('avatar', {
        size: resolvedSize
      }), stylex.props(styles.wrapper, avatarGroup && groupStyles.ring, avatarGroup && groupStyles.overlap, avatarGroup && groupDynamicStyles.overlap(-avatarGroup.overlap), xstyle), className, style),
      children: [/*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.content, dynamicStyles.size(numericSize)),
        children: [showImage && /*#__PURE__*/_jsx("img", {
          src: src,
          alt: "",
          onError: () => setErroredSrc(src),
          ...{
            className: "xh8yej3 x5yr21d xl1xv1r"
          }
        }), showFallbackImage && /*#__PURE__*/_jsx("img", {
          src: fallbackSrc,
          alt: "",
          onError: () => setErroredFallbackSrc(fallbackSrc),
          ...{
            className: "xh8yej3 x5yr21d xl1xv1r"
          }
        }), showInitials && /*#__PURE__*/_jsx("div", {
          ...stylex.props(styles.fallback, dynamicStyles.fontSize(numericSize)),
          children: getInitials(name)
        }), showIcon && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k xh8yej3 x5yr21d x17x4s8c xv1l7n4 x9ynric x1e4wzip xtvhhri"
          },
          children: /*#__PURE__*/_jsx(DefaultIcon, {
            size: numericSize
          })
        })]
      }), status && /*#__PURE__*/_jsx("div", {
        ...stylex.props(styles.status, dynamicStyles.statusPosition(numericSize)),
        children: status
      })]
    })
  });
}
Avatar.displayName = 'Avatar';